// The playground's API client (PG-1, owner 2026-10-18: "postman type which api we build i can test"): pick an endpoint,
// type the input, Send, and see the call the handler made to the lender, the lender's raw reply and the handler's answer.
// Two targets: the SANDBOX (the code in the editor, unsaved too; the real lender or a reply typed here) and the BUILT
// CONTAINER (what the Hub address runs, through the Hub with its key). Saved requests are the lender's, shared by the team.
// Test applicants only: the input goes to the lender, and a saved request is kept in the Hub.
import { useState } from 'react'
import { ChevronDown, ChevronRight, Code2, Copy, Plus, Save, Send, Trash2, Wand2 } from 'lucide-react'
import { Badge, Can, ConfirmDialog, JsonView, PromptDialog, SegmentedControl, formatDateTime, notify } from '@/kit/ui'
import { type Draft, type Parsed, type SavedRequest, curlOf, inputProblems, parseDraft } from '@/lib/apiClient'
import type { RunResult } from '@/lib/agent'
import type { ManifestEndpoint } from '@/types'
import { CodeEditor } from '../CodeEditor'

export const CLIENT_TAB = 'api-client:'
export type ClientTarget = 'sandbox' | 'container'
/** One Send: what answered and what it said. */
export interface ClientResult {
  target: ClientTarget
  endpoint: string
  environment: 'uat' | 'production'
  run: RunResult
  at: string
  /** the built container: which version and container answered, and the Hub address it stands for */
  version?: string
  container?: string
  address?: string
  /** a typed lender reply was used (sandbox only) */
  typedReply?: boolean
}

/** The side view's "My APIs" folder (PG-2c: one list with the HTTP requests): every endpoint of the batch, its saved requests under it. */
export function ApiClientView({ version, endpoints, saved, current, canDelete, onOpen, onOpenSaved, onDelete }: {
  version: string
  endpoints: ManifestEndpoint[]
  saved: SavedRequest[]
  current: Draft | null
  canDelete: boolean
  onOpen: (route: string) => void
  onOpenSaved: (r: SavedRequest) => void
  onDelete: (r: SavedRequest) => void
}) {
  const [deleting, setDeleting] = useState<SavedRequest | null>(null)
  const [open, setOpen] = useState(true)
  const orphans = saved.filter((r) => !endpoints.some((e) => e.path === r.endpoint))
  const row = (r: SavedRequest) => (
    <li key={r.id} className={`group flex items-center gap-1.5 py-0.5 pl-7 pr-2 hover:bg-slate-200/70 dark:hover:bg-slate-800 ${current?.savedId === r.id ? 'bg-primary-soft/50 dark:bg-primary-soft/10' : ''}`}>
      <button type="button" className="min-w-0 flex-1 text-left" onClick={() => onOpenSaved(r)} title={`Saved by ${r.savedBy} · ${formatDateTime(r.savedAt)}`}>
        <span className="block truncate">{r.name}{r.reply && <Badge tone="violet" className="ml-1 !py-0">typed reply</Badge>}</span>
        <span className="block truncate text-[10px] text-muted">{r.savedBy.split('@')[0]} · {formatDateTime(r.savedAt)}</span>
      </button>
      {canDelete && <button type="button" className="icon-btn hidden !p-0.5 group-hover:inline-flex" aria-label={`Delete saved request ${r.name}`} onClick={() => setDeleting(r)}><Trash2 size={11} /></button>}
    </li>
  )
  return (
    <div className="shrink-0" data-testid="pg-client-view">
      <button type="button" className="flex h-8 w-full items-center gap-1 px-2 text-left text-[11px] font-semibold uppercase tracking-wider text-muted hover:text-default" onClick={() => setOpen(!open)} aria-expanded={open} title="Your handler code: Send runs it (unsaved too) or the built container, with a test applicant's input. Test applicants only, never a real person.">
        {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}<Code2 size={12} className="text-violet-600 dark:text-violet-300" /> My APIs · {version}
      </button>
      {open && <ul className="pb-2 text-[12px]" data-testid="client-endpoints">
        {endpoints.map((e) => (
          <li key={e.path} className="mb-1">
            <div className={`group flex items-center gap-1.5 px-2 py-0.5 hover:bg-slate-200/70 dark:hover:bg-slate-800 ${current?.endpoint === e.path && !current.savedId ? 'bg-primary-soft/50 dark:bg-primary-soft/10' : ''}`}>
              <span className="w-10 shrink-0 font-mono text-[10px] font-bold text-violet-600 dark:text-violet-300">{e.method}</span>
              <button type="button" className="min-w-0 flex-1 truncate text-left font-medium" onClick={() => onOpen(e.path)} title={e.title ?? e.path}>{e.path}</button>
              <button type="button" className="icon-btn hidden !p-0.5 group-hover:inline-flex" aria-label={`New request to ${e.method} ${e.path}`} onClick={() => onOpen(e.path)}><Plus size={12} /></button>
            </div>
            <ul>{saved.filter((r) => r.endpoint === e.path).map(row)}</ul>
          </li>
        ))}
        {!endpoints.length && <li className="px-3 text-[11px] text-muted">No endpoint in this batch yet: New file → Endpoint.</li>}
        {orphans.length > 0 && (
          <li className="mt-2">
            <div className="px-3 text-[11px] font-semibold uppercase tracking-wider text-muted">Not in this version</div>
            <ul>{orphans.map(row)}</ul>
          </li>
        )}
      </ul>}
      <ConfirmDialog isOpen={deleting !== null} onClose={() => setDeleting(null)} type="danger" title={`Delete “${deleting?.name ?? ''}”?`} message="The saved request goes for everyone on this lender's team." confirmText="Delete" onConfirm={() => { if (deleting) onDelete(deleting); setDeleting(null) }} />
    </div>
  )
}

const statusTone = (s: number) => (s === 0 ? 'danger' : s < 300 ? 'success' : s < 500 ? 'warning' : 'danger')
const Section = ({ title, right, children }: { title: string; right?: React.ReactNode; children: React.ReactNode }) => (
  <section className="rounded-md border border-slate-200 dark:border-slate-800">
    <div className="flex items-center justify-between gap-2 border-b border-slate-200 bg-slate-50 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted dark:border-slate-800 dark:bg-slate-900">{title}{right}</div>
    <div className="space-y-2 p-3 text-xs">{children}</div>
  </section>
)
const Headers = ({ h }: { h?: Record<string, string> }) =>
  h && Object.keys(h).length ? (
    <table className="w-full font-mono text-[11px]"><tbody>{Object.entries(h).map(([k, v]) => <tr key={k} className="align-top"><td className="w-1/3 py-px pr-2 text-muted">{k}</td><td className="break-all py-px">{v}</td></tr>)}</tbody></table>
  ) : <p className="text-[11px] text-muted">no headers</p>

/** The request and its answer: the editor tab of the API client. */
export function ApiClient({ slug, endpoints, draft, onDraft, target, onTarget, environment, sandboxReady, containerAvailable, sending, result, canSend, canSave, canMakeTest, hubBase, lenderAddress, onSend, onSave, onMakeTestEvent }: {
  slug: string
  /** the lender's address in this environment (Lender info): where a real call goes */
  lenderAddress: string | null
  endpoints: ManifestEndpoint[]
  draft: Draft
  onDraft: (d: Draft) => void
  target: ClientTarget
  onTarget: (t: ClientTarget) => void
  environment: 'uat' | 'production'
  sandboxReady: boolean
  /** the Hub API is there (the fixtures have no built container) */
  containerAvailable: boolean
  sending: boolean
  result: ClientResult | null
  canSend: boolean
  canSave: boolean
  /** the open version takes new test events (draft / tests passed, an editor) */
  canMakeTest: boolean
  /** Settings → Hub addresses of this environment, for Copy as curl */
  hubBase: string
  onSend: (p: Parsed) => void
  onSave: (name: string, p: Parsed) => void
  onMakeTestEvent: (reply: { status: number; body: unknown }, outcome: string | null) => void
}) {
  const [tab, setTab] = useState<'input' | 'prev' | 'reply'>('input')
  const [naming, setNaming] = useState(false)
  const ep = endpoints.find((e) => e.path === draft.endpoint)
  const parsed = parseDraft(draft)
  const problems = parsed.ok ? inputProblems(parsed.value.input) : []
  const blocked = target === 'container' && problems.length > 0
  const typed = target === 'sandbox' && draft.reply !== null
  const ready = target === 'sandbox' ? sandboxReady : containerAvailable
  const set = (patch: Partial<Draft>) => onDraft({ ...draft, ...patch })

  const send = () => {
    if (!canSend || sending) return
    if (!parsed.ok) return void notify.error('Not sent', parsed.error)
    if (blocked) return void notify.error('Not sent', 'The built container takes the standard input only: fix the fields listed under the input.')
    if (!ready) return void notify.error('Not sent', target === 'sandbox' ? 'The sandbox is not ready yet.' : 'The built container needs the Lender Hub API.')
    onSend(parsed.value)
  }
  const save = (asNew = false) => {
    if (!canSave) return
    if (!parsed.ok) return void notify.error('Not saved', parsed.error)
    if (!draft.name || asNew) return setNaming(true)
    onSave(draft.name, parsed.value)
  }
  const curl = parsed.ok ? curlOf(hubBase, slug, draft.endpoint, parsed.value) : ''
  const editorText = tab === 'input' ? draft.input : tab === 'prev' ? draft.prev : draft.reply?.body ?? ''
  const editText = (v: string) => (tab === 'input' ? set({ input: v }) : tab === 'prev' ? set({ prev: v }) : set({ reply: { status: draft.reply?.status ?? '200', body: v } }))

  const run = result?.run
  const value = run?.value as { reply_code?: string; message?: string; lender_ref?: string } | undefined

  return (
    <div className="flex h-full min-h-0 flex-col" data-testid="pg-client">
      {/* the request line */}
      <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 px-3 py-2 dark:border-slate-800">
        <span className="font-mono text-xs font-bold text-violet-600 dark:text-violet-300">{ep?.method ?? 'POST'}</span>
        <select className="input !min-h-8 w-auto max-w-[18rem] py-0 font-mono text-xs" aria-label="Endpoint" value={draft.endpoint} onChange={(e) => set({ endpoint: e.target.value, savedId: null, name: '' })}>
          {endpoints.map((e) => <option key={e.path} value={e.path}>{e.path}{e.title ? ` · ${e.title}` : ''}</option>)}
          {!ep && <option value={draft.endpoint}>{draft.endpoint} (not in this version)</option>}
        </select>
        <SegmentedControl size="sm" ariaLabel="Target" value={target} onChange={onTarget} options={[{ value: 'sandbox', label: 'Sandbox: my code' }, { value: 'container', label: 'Built container' }]} />
        <span className={`rounded px-1.5 py-0.5 text-2xs font-semibold ${environment === 'production' ? 'bg-red-600 text-white' : 'bg-slate-200 text-slate-700 dark:bg-slate-800 dark:text-slate-200'}`} title="The environment of the playground (top bar)">{environment === 'production' ? 'PRODUCTION' : 'UAT'}</span>
        <div className="flex-1" />
        {draft.name && <span className="max-w-[12rem] truncate text-2xs text-muted" title="The saved request open now">“{draft.name}”</span>}
        <Can permission="code.test">
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => save()} disabled={!canSave} data-testid="client-save" title={draft.name ? `Save over “${draft.name}”` : 'Save with a name, for your team'}><Save size={12} /> Save</button>
          {draft.name && <button type="button" className="btn btn-secondary btn-sm" onClick={() => save(true)} disabled={!canSave}>Save as…</button>}
        </Can>
        <button type="button" className="btn btn-secondary btn-sm" disabled={!curl} onClick={async () => { try { await navigator.clipboard.writeText(curl); notify.success('Copied', 'The curl for the Hub address; put your x-hub-key in place of <your key>.') } catch { notify.error('Not copied', 'The browser did not allow the clipboard.') } }} title={curl || 'Fix the JSON first'} data-testid="client-curl"><Copy size={12} /> curl</button>
        <Can permission="code.test">
          <button type="button" className="btn btn-primary btn-sm" onClick={send} disabled={!canSend || sending || !ready || blocked} data-testid="client-send" title="Send (Ctrl+Enter in the editor)"><Send size={12} /> {sending ? 'Sending…' : 'Send'}</button>
        </Can>
      </div>
      <p className="shrink-0 border-b border-slate-200 bg-slate-50 px-3 py-1 text-[11px] text-muted dark:border-slate-800 dark:bg-slate-900/60" data-testid="client-target-note">
        {target === 'sandbox'
          ? <>Runs <b>the code in the editor</b> (unsaved too) in this version's sandbox, with the lender's {environment === 'production' ? 'production' : 'UAT'} variables. Lender reply: {typed ? <b>the one typed in the Lender reply tab</b> : <b>the real lender</b>}.{!sandboxReady && ' · sandbox not ready'}</>
          : <>Runs <b>what the Hub address runs</b> ({environment === 'production' ? 'the live version' : 'the newest built version'}), through the Hub with its key: the same answer Postman gets. Real lender calls.{!containerAvailable && ' · needs the Lender Hub API'}</>}
      </p>
      {!typed && (
        <p className="shrink-0 border-b border-amber-200 bg-amber-50 px-3 py-1 text-[11px] text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200" data-testid="client-lender-address">
          {lenderAddress
            ? <>Send calls <b className="font-mono">{lenderAddress}</b>: the lender receives this input. Use the lender's test applicant only.</>
            : <>No lender address for {environment === 'production' ? 'production' : 'UAT'} in Lender info: a real call cannot go out. Type a reply in the Lender reply tab, or set the address.</>}
        </p>
      )}

      <div className="min-h-0 flex-1 overflow-y-auto">
        {/* the request */}
        <div className="flex items-center gap-1 border-b border-slate-200 px-3 dark:border-slate-800" role="tablist" aria-label="Request">
          {([['input', 'Input (applicant)'], ['prev', 'prev'], ['reply', 'Lender reply']] as const).map(([id, label]) => (
            <button key={id} type="button" role="tab" aria-selected={tab === id} disabled={id === 'reply' && target === 'container'} onClick={() => setTab(id)} className={`border-b-2 px-2 py-1.5 text-xs disabled:opacity-40 ${tab === id ? 'border-primary font-semibold' : 'border-transparent text-muted hover:text-default'}`}>
              {label}{id === 'reply' && typed ? ' ●' : ''}
            </button>
          ))}
        </div>
        {tab === 'reply' && (
          <div className="flex flex-wrap items-center gap-2 px-3 py-2 text-xs">
            <label className="flex items-center gap-1"><input type="radio" name="reply-from" checked={draft.reply === null} onChange={() => set({ reply: null })} /> The real lender</label>
            <label className="flex items-center gap-1"><input type="radio" name="reply-from" checked={draft.reply !== null} onChange={() => set({ reply: { status: '200', body: '{\n  \n}' } })} data-testid="client-typed-reply" /> A reply I type (the lender is not called)</label>
            {draft.reply && <label className="flex items-center gap-1">HTTP status <input className="input !min-h-7 w-20 py-0" aria-label="Lender reply status" value={draft.reply.status} onChange={(e) => set({ reply: { ...draft.reply!, status: e.target.value } })} /></label>}
          </div>
        )}
        {tab === 'reply' && !draft.reply ? (
          <p className="px-3 pb-3 text-xs text-muted">The handler's <code>ctx.lender()</code> calls the lender at the address in its variables. For a lender without a test server, type the reply instead.</p>
        ) : (
          <div className="border-b border-slate-200 dark:border-slate-800" style={{ height: 230 }}>
            <CodeEditor path={`request/${tab}.json`} value={editorText} readOnly={!canSend} onChange={editText} onRun={send} onSave={() => save()} height={230} />
          </div>
        )}
        {tab === 'prev' && <p className="px-3 py-1 text-[11px] text-muted">ctx.prev: the earlier step's outcome (a status check reads <code>lender_ref</code> from it). Empty = none.</p>}
        {!parsed.ok && <p className="px-3 py-1 text-xs text-red-600" role="alert">{parsed.error}</p>}
        {problems.length > 0 && (
          <div className={`mx-3 my-2 rounded-md px-3 py-2 text-[11px] ${blocked ? 'bg-red-50 text-red-800 dark:bg-red-950/40 dark:text-red-200' : 'bg-amber-50 text-amber-900 dark:bg-amber-950/40 dark:text-amber-200'}`} data-testid="client-input-problems">
            <b>{blocked ? 'Not the standard input' : 'Not the standard input (the sandbox still runs it)'}:</b> what the website and app send has other fields.
            <ul className="mt-1 list-disc pl-4">{problems.slice(0, 8).map((p) => <li key={p}>{p}</li>)}</ul>
          </div>
        )}

        {/* the answer */}
        <div className="space-y-3 p-3" data-testid="client-result">
          {!result && !sending && <p className="text-xs text-muted">Press <b>Send</b>: the call to the lender, its reply and the handler's answer show here.</p>}
          {sending && <p className="animate-pulse text-xs text-muted">Sending…</p>}
          {result && run && (
            <>
              <div className="flex flex-wrap items-center gap-2 text-xs" data-testid="client-summary">
                <Badge tone={run.ok ? 'success' : 'danger'}>{run.ok ? '✓ approved' : '✗ not approved'}</Badge>
                <span>outcome <b data-testid="client-outcome">{run.outcome ?? 'none'}</b>{value?.reply_code ? <> · reply_code <b>{value.reply_code}</b></> : null}</span>
                <span className="text-muted">· {run.ms} ms · {result.environment === 'production' ? 'production' : 'UAT'} · {result.at}</span>
                <span className="text-muted">· {result.target === 'sandbox' ? `sandbox (your code)${result.typedReply ? ', typed reply' : ''}` : `${result.container} · version ${result.version}`}</span>
              </div>
              {result.address && <p className="break-all font-mono text-[11px] text-muted">Hub address: {result.address}</p>}
              {run.error && <p className="rounded-md bg-red-50 px-3 py-2 text-xs text-red-800 dark:bg-red-950/40 dark:text-red-200" role="alert">{run.error}</p>}
              {run.envMissing.length > 0 && <p className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:bg-amber-950/40 dark:text-amber-200">Missing variables: <b>{run.envMissing.join(', ')}</b>. Give them a value in the lender's Env variables tab{run.envMissing.includes('LENDER_BASE_URL') ? ' (LENDER_BASE_URL comes from Lender info: the UAT / production address)' : ''}.</p>}

              {run.calls.map((c, i) => (
                <div key={i} className="space-y-3">
                  <Section title={run.calls.length > 1 ? `Call ${i + 1} to the lender` : 'The call to the lender'}>
                    <p className="break-all font-mono"><b className="text-violet-600 dark:text-violet-300">{c.call.method}</b> {c.call.url}{c.call.timeoutMs ? <span className="text-muted"> · timeout {c.call.timeoutMs} ms</span> : null}</p>
                    <Headers h={c.call.headers} />
                    {c.call.body !== undefined && c.call.body !== null && <JsonView title="Body" value={c.call.body} maxHeight={220} />}
                  </Section>
                  <Section title="The lender's reply" right={canMakeTest && result.target === 'sandbox' && !result.typedReply ? <button type="button" className="btn btn-secondary btn-sm !normal-case !tracking-normal" onClick={() => onMakeTestEvent({ status: c.reply.status, body: c.reply.body }, run.outcome)} data-testid="client-make-test"><Wand2 size={11} /> Make a test event</button> : undefined}>
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge tone={statusTone(c.reply.status)}>{c.reply.status === 0 ? 'no reply' : `HTTP ${c.reply.status}`}</Badge>
                      {c.reply.ms !== undefined && <span className="text-muted">{c.reply.ms} ms</span>}
                      {c.reply.error && <span className="text-red-600">{c.reply.error}</span>}
                    </div>
                    <Headers h={c.reply.headers} />
                    <JsonView title="Body" value={c.reply.body ?? null} maxHeight={260} />
                  </Section>
                  {c.issues.length > 0 && <ul className="space-y-0.5 text-xs">{c.issues.map((x, j) => <li key={j} className={x.level === 'error' ? 'text-red-600' : 'text-amber-700 dark:text-amber-300'}>{x.level === 'error' ? '✗' : '⚠'} the call: {x.text}</li>)}</ul>}
                </div>
              ))}
              {!run.calls.length && !run.error && <p className="text-xs text-muted">The handler did not call the lender.</p>}

              <Section title="The handler's answer">
                {run.value !== undefined ? <JsonView value={run.value} maxHeight={240} /> : <p className="text-muted">nothing returned</p>}
                {run.issues.length > 0 && <ul className="space-y-0.5">{run.issues.map((x, j) => <li key={j} className={x.level === 'error' ? 'text-red-600' : 'text-amber-700 dark:text-amber-300'}>{x.level === 'error' ? '✗' : '⚠'} system check: {x.text}</li>)}</ul>}
                {run.log.length > 0 && <pre className="code-block" style={{ maxHeight: 160 }}>{run.log.join('\n')}</pre>}
              </Section>
            </>
          )}
        </div>
      </div>

      <PromptDialog
        isOpen={naming}
        onClose={() => setNaming(false)}
        title={`Save a request to ${ep?.method ?? ''} ${draft.endpoint}`}
        message="Your team sees it under this endpoint in the API client. Test applicants only. The same name replaces the saved one."
        inputLabel="Name"
        initialValue={draft.name}
        validate={(v) => (v.trim().length < 2 ? 'At least 2 characters, e.g. Wrong PAN' : v.trim().length > 80 ? 'At most 80 characters' : null)}
        confirmText="Save"
        onConfirm={(v) => { setNaming(false); if (parsed.ok) onSave(v.trim(), parsed.value) }}
      />
    </div>
  )
}
